import { useEffect, useRef, useState, useCallback } from "react";
// @ts-expect-error - No types definition available for Flussonic WebRTC Player
import { Player, PLAYER_EVENTS } from "@flussonic/flussonic-webrtc-player";
import * as Sentry from "@sentry/browser";
import {
  getWebRTCPlayerBaseOptions,
  isWhipwhapEnabled,
} from "./webrtcPlayerOptions";
import type { PlayerStatus, PlayerHookReturn } from "../types";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";
import { serializeFlussonicError, toSentryError } from "../flussonicErrorSerializer";
import {
  buildPlayerFailureTags,
  getPlayerFailureDiagnostics,
} from "../playerNetworkDiagnostics";

interface FlussonicEvent {
  type: string;
  [key: string]: unknown;
}

interface FlussonicError {
  message?: string;
  [key: string]: unknown;
}

export const MAX_RTC_RETRIES = 3;
export const RETRY_DELAY_MS = 1500;
const MAX_RETRY_DELAY_MS = 30000;

// A WebRTC connection that reaches PLAY but drops shortly after (e.g. corporate
// firewalls dropping UDP consent traffic → ice_connection_closed at ~15s) must NOT
// reset the retry budget, or context-level auto-fallback can never trigger. Only
// treat the connection as healthy once it survives this window. Must exceed the
// ~15s ICE consent-freshness timeout.
const CONNECTION_STABILITY_WINDOW_MS = 30000;

function getRetryDelay(attempt: number): number {
  const exponential = RETRY_DELAY_MS * Math.pow(2, attempt);
  const capped = Math.min(exponential, MAX_RETRY_DELAY_MS);
  // Jitter: 50-100% of capped value to avoid thundering herd
  return Math.round(capped * (0.5 + Math.random() * 0.5));
}

// Whipwhap workaround for Flussonic 23 bug
// Known issue: First connection with whipwhap enabled often drops within 10s, but retry succeeds
const WHIPWHAP_FIRST_CONNECTION_GRACE_PERIOD_MS = 10000; // 10 seconds

// Transient WebRTC errors that indicate network disruptions, not permanent failures.
// These are downgraded to Sentry breadcrumbs instead of captureException to reduce noise.
const TRANSIENT_WEBRTC_ERRORS = ["ice_connection_closed", "ws_closed", "ws_error", "peer_connection_closed"];

function isTransientWebRTCError(message: string): boolean {
  const lower = message.toLowerCase();
  return TRANSIENT_WEBRTC_ERRORS.some((e) => lower.includes(e));
}

interface UseWebRTCPlayerOptions {
  audioElement: HTMLAudioElement | null;
  address: string | null;
  enabled: boolean; // Only connect when enabled
  authenticated: boolean;
}

/**
 * React hook for managing WebRTC audio player
 *
 * Handles connection, retries, and cleanup automatically
 */
export const useWebRTCPlayer = ({
  audioElement,
  address,
  enabled,
  authenticated,
}: UseWebRTCPlayerOptions): PlayerHookReturn => {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const playerRef = useRef<Player | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const currentAddressRef = useRef<string | null>(null);
  const connectionIdRef = useRef<number>(0); // Track connection attempts to ignore stale handlers

  // Whipwhap workaround refs
  const whipwhapFirstAttemptRef = useRef<boolean>(false);
  const connectionStartTimeRef = useRef<number>(0);
  const silentRetryTimeoutRef = useRef<number | null>(null);

  // Stability window: defers resetting the retry budget until a connection proves it
  // can stay up, so flapping connections accumulate retries and trigger auto-fallback.
  const stabilityTimeoutRef = useRef<number | null>(null);

  // Set volume helper
  const setVolume = useCallback(
    (volume: number) => {
      if (audioElement) {
        audioElement.volume = volume;
      }
    },
    [audioElement]
  );

  // Helper: Clean up a specific player instance
  const cleanupPlayer = useCallback((player: Player, context: string) => {
    try {
      player.stop();
      player.destroy();
    } catch (e) {
      console.debug(`Error cleaning up player (${context}):`, e);
    }
  }, []);

  // Helper: Check if a connection ID is stale
  const isStaleConnection = useCallback((connectionId: number): boolean => {
    return connectionIdRef.current !== connectionId;
  }, []);

  // Helper: Check if we're within whipwhap grace period
  const isWithinWhipwhapGracePeriod = useCallback(
    (whipwhapEnabled: boolean): boolean => {
      const timeSinceConnectionStart =
        Date.now() - connectionStartTimeRef.current;
      return (
        whipwhapEnabled &&
        whipwhapFirstAttemptRef.current &&
        timeSinceConnectionStart < WHIPWHAP_FIRST_CONNECTION_GRACE_PERIOD_MS
      );
    },
    []
  );

  // Helper: Schedule a silent retry for whipwhap grace period failures
  const scheduleWhipwhapSilentRetry = useCallback(
    (
      thisConnectionId: number,
      targetAddress: string,
      targetAudioElement: HTMLAudioElement,
      connectWebRTC: (
        id: number,
        address: string,
        element: HTMLAudioElement
      ) => Promise<void>
    ) => {
      silentRetryTimeoutRef.current = setTimeout(() => {
        if (connectionIdRef.current === thisConnectionId) {
          setRetryCount((prev) => prev + 1);
          connectionIdRef.current++;
          const newConnectionId = connectionIdRef.current;
          console.log(
            `[WebRTC Silent Retry #${newConnectionId}] Retrying after whipwhap grace period failure`
          );
          connectWebRTC(newConnectionId, targetAddress, targetAudioElement);
        }
      }, 1000);
    },
    []
  );

  // Cleanup function
  const cleanup = useCallback(() => {
    if (playerRef.current) {
      cleanupPlayer(playerRef.current, "full cleanup");
      playerRef.current = null;
    }

    if (audioElement) {
      try {
        audioElement.srcObject = null;
      } catch (e) {
        console.debug("Error clearing audio element:", e);
      }
    }

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (silentRetryTimeoutRef.current) {
      clearTimeout(silentRetryTimeoutRef.current);
      silentRetryTimeoutRef.current = null;
    }

    if (stabilityTimeoutRef.current) {
      clearTimeout(stabilityTimeoutRef.current);
      stabilityTimeoutRef.current = null;
    }

    // Clear the address ref on cleanup
    currentAddressRef.current = null;
  }, [audioElement, cleanupPlayer]);

  // Extracted WebRTC connection logic - used by both initial connection and retries
  // Dependencies:
  // - All setState functions (setStatus, setError) are guaranteed stable by React
  // - All refs (playerRef, connectionIdRef, whipwhapFirstAttemptRef, etc.) are stable
  // - retryCount is needed to determine first connection attempt for whipwhap workaround
  const connectWebRTC = useCallback(
    async (
      thisConnectionId: number,
      targetAddress: string,
      targetAudioElement: HTMLAudioElement
    ) => {
      // Read whipwhap setting from environment variable
      const whipwhapEnabled = isWhipwhapEnabled();

      console.log(
        `[WebRTC] Whipwhap mode: ${whipwhapEnabled} (from VITE_WHIPWHAP_ENABLED: ${import.meta.env.VITE_WHIPWHAP_ENABLED})`
      );
      diagnosticEventLog.record("webrtc:audio", "info", "connection-start", { connectionId: thisConnectionId, address: targetAddress, whipwhapEnabled });

      // Track connection start time for whipwhap workaround
      if (whipwhapEnabled && retryCount === 0) {
        whipwhapFirstAttemptRef.current = true;
        connectionStartTimeRef.current = Date.now();
        console.log(
          "[WebRTC] Whipwhap first attempt - grace period active for 10s"
        );
      }

      // Get base options and add event handler
      const options = {
        ...getWebRTCPlayerBaseOptions(whipwhapEnabled),

        // HTMLMediaElement event handler
        // Player emits: stalled, playing, waiting, pause
        // Note: "connection state" events are Publisher-only, not emitted by Player
        onEvent: (evt: FlussonicEvent) => {
          console.log(
            `[WebRTC Connection #${thisConnectionId}] Player event:`,
            evt
          );

          // Ignore events from stale connections
          if (isStaleConnection(thisConnectionId)) {
            console.log(
              `[WebRTC Connection #${thisConnectionId}] Ignoring event from stale connection:`,
              evt
            );
            diagnosticEventLog.record("webrtc:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: evt.type });
            return;
          }

          // Log player state events for debugging
          // Real error handling happens in PLAYER_EVENTS.FAIL handler below
          if (evt.type === "stalled") {
            console.warn(
              `[WebRTC Connection #${thisConnectionId}] Player stalled (buffering/network issue)`
            );
            diagnosticEventLog.record("webrtc:audio", "warn", "stalled", { connectionId: thisConnectionId });
          }
        },
      };

      try {
        const newPlayer = new Player(targetAudioElement, targetAddress, {
          ...options,
        });

        // Store player reference
        playerRef.current = newPlayer;

        // Expose for debugging
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).squawkPlayer = newPlayer;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).onEventCallback = options.onEvent;

        newPlayer.statsSendEnable = options.statsSendEnable;

        // Handle player failure
        newPlayer.on(PLAYER_EVENTS.FAIL, (error: FlussonicError) => {
          // Ignore errors from stale connections
          if (isStaleConnection(thisConnectionId)) {
            console.log(
              `[WebRTC Connection #${thisConnectionId}] Ignoring FAIL event from stale connection:`,
              error
            );
            diagnosticEventLog.record("webrtc:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: "FAIL" });
            return;
          }

          // Check if this is within whipwhap grace period
          const timeSinceConnectionStart =
            Date.now() - connectionStartTimeRef.current;

          if (isWithinWhipwhapGracePeriod(whipwhapEnabled)) {
            console.log(
              `[WebRTC] Whipwhap first connection FAIL after ${timeSinceConnectionStart}ms (known Flussonic 23 bug) - silently retrying...`
            );
            diagnosticEventLog.record("webrtc:audio", "warn", "whipwhap-grace-retry", { connectionId: thisConnectionId, timeSinceStartMs: timeSinceConnectionStart });

            whipwhapFirstAttemptRef.current = false; // Only apply grace period once

            // Clean up the failed player
            cleanupPlayer(newPlayer, "whipwhap grace period retry");

            // Trigger immediate retry without showing error state
            scheduleWhipwhapSilentRetry(
              thisConnectionId,
              targetAddress,
              targetAudioElement,
              connectWebRTC
            );

            return; // Don't process as normal error
          }

          // play_promise_reject is Flussonic's wrapper for AbortError on play() — expected during reconnection
          if (error && typeof error === "object" && (error as Record<string, unknown>).error === "play_promise_reject") {
            console.debug(`[WebRTC Connection #${thisConnectionId}] play() interrupted (Flussonic play_promise_reject) — expected`);
            diagnosticEventLog.record("webrtc:audio", "info", "play-interrupted", { connectionId: thisConnectionId });
            return;
          }

          // Normal error handling (outside grace period)
          const { message: errMsg, details: errDetails } = serializeFlussonicError(error);
          console.error(
            `[WebRTC Connection #${thisConnectionId}] WebRTC connection failed:`,
            errMsg
          );
          diagnosticEventLog.record("webrtc:audio", "error", "connection-failed", { connectionId: thisConnectionId, error: errMsg, whipwhapEnabled });

          if (isTransientWebRTCError(errMsg)) {
            // Transient network errors — log as breadcrumb, not exception
            Sentry.addBreadcrumb({
              category: "webrtc",
              message: `WebRTC connection #${thisConnectionId} failed: ${errMsg}`,
              level: "warning",
              data: { connectionId: thisConnectionId, whipwhapEnabled },
            });
          } else {
            // Non-transient errors (e.g. set_remote_description_failed) — capture as exception
            const diagnostics = getPlayerFailureDiagnostics(targetAddress, "webrtc", errMsg);
            Sentry.captureException(toSentryError(error), {
              tags: buildPlayerFailureTags(
                "useWebRTCPlayer",
                "webrtc_player_fail",
                "webrtc",
                diagnostics
              ),
              extra: {
                address: targetAddress,
                errorDetails: errDetails,
                connectionId: thisConnectionId,
                whipwhapEnabled,
                timeSinceStart: timeSinceConnectionStart,
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                networkDiagnostics: diagnostics.network,
              },
              fingerprint: ["useWebRTCPlayer", errMsg],
            });
          }

          // Clean up the failed player
          cleanupPlayer(newPlayer, "normal failure");

          // Connection died before proving stable — cancel the pending budget reset so this
          // failure counts toward the retry limit (and eventually auto-fallback).
          if (stabilityTimeoutRef.current) {
            clearTimeout(stabilityTimeoutRef.current);
            stabilityTimeoutRef.current = null;
          }

          setStatus("error");
          setError(toSentryError(error));
        });

        // Handle successful play
        newPlayer.on(PLAYER_EVENTS.PLAY, () => {
          // Ignore play events from stale connections
          if (isStaleConnection(thisConnectionId)) {
            console.log(
              `[WebRTC Connection #${thisConnectionId}] Ignoring PLAY event from stale connection`
            );
            diagnosticEventLog.record("webrtc:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: "PLAY" });
            return;
          }

          console.log(
            `[WebRTC Connection #${thisConnectionId}] WebRTC connection established (PLAY event)`
          );
          diagnosticEventLog.record("webrtc:audio", "info", "connected", { connectionId: thisConnectionId });

          // Connection successful - clear whipwhap grace period
          whipwhapFirstAttemptRef.current = false;

          setStatus("connected");
          setError(null);

          // Defer the retry-budget reset until the connection proves stable. A connection
          // that drops before this window (e.g. corporate firewalls killing UDP consent at
          // ~15s) must keep its accumulated retryCount so context-level auto-fallback can
          // eventually trigger. The stale-connection guard means a superseded connection's
          // timer won't reset the wrong counter.
          if (stabilityTimeoutRef.current) clearTimeout(stabilityTimeoutRef.current);
          stabilityTimeoutRef.current = window.setTimeout(() => {
            if (isStaleConnection(thisConnectionId)) return;
            diagnosticEventLog.record("webrtc:audio", "info", "connection-stable", {
              connectionId: thisConnectionId,
              stableForMs: CONNECTION_STABILITY_WINDOW_MS,
            });
            setRetryCount(0);
          }, CONNECTION_STABILITY_WINDOW_MS);
        });

        // Start playing
        await newPlayer.play();

        // Note: We don't set status here because the PLAY event handler does it
        // This ensures the "connecting" state is visible in the UI
      } catch (err) {
        // AbortError from play() interruption during cleanup/reconnection is expected
        if (err instanceof DOMException && err.name === "AbortError") {
          console.debug(`[WebRTC Connection #${thisConnectionId}] play() interrupted (AbortError) — expected during cleanup`);
          diagnosticEventLog.record("webrtc:audio", "info", "play-interrupted", { connectionId: thisConnectionId });
          return;
        }

        const { message: errorMessage, details: errorDetails } = serializeFlussonicError(err);
        console.error(
          `[WebRTC Connection #${thisConnectionId}] Connection error:`,
          errorMessage
        );
        diagnosticEventLog.record("webrtc:audio", "error", "connection-error", { connectionId: thisConnectionId, error: errorMessage });

        const connErrDiagnostics = getPlayerFailureDiagnostics(targetAddress, "webrtc", errorMessage);
        Sentry.captureException(toSentryError(err), {
          tags: buildPlayerFailureTags(
            "useWebRTCPlayer",
            "webrtc_connection_error",
            "webrtc",
            connErrDiagnostics
          ),
          extra: {
            address: targetAddress,
            errorDetails,
            connectionId: thisConnectionId,
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            networkDiagnostics: connErrDiagnostics.network,
          },
          fingerprint: ["useWebRTCPlayer", errorMessage],
        });

        setStatus("error");
        setError(toSentryError(err));
      }
    },
    [
      retryCount,
      isStaleConnection,
      isWithinWhipwhapGracePeriod,
      cleanupPlayer,
      scheduleWhipwhapSilentRetry,
    ]
  );

  // Connection effect
  // Note: We intentionally read `status` without depending on it (eslint-disable below)
  // This is SAFE because:
  // 1. Status changes to "error" are handled by the separate retry effect
  // 2. If connection params (address, enabled, etc.) change, this effect re-runs
  // 3. The status read is just an optimization to avoid unnecessary reconnects
  // 4. React setState functions (setStatus, setError) are guaranteed stable
  useEffect(() => {
    // Don't connect if not enabled, not authenticated, or missing dependencies
    if (!enabled || !authenticated || !address || !audioElement) {
      // Reset state when disabled
      const reason = !enabled ? "not-enabled" : !authenticated ? "not-authenticated" : !address ? "no-address" : "no-audio-element";
      diagnosticEventLog.record("webrtc:audio", "info", "disabled", { reason });
      setStatus("idle");
      setError(null);
      setRetryCount(0);
      cleanup(); // This also clears currentAddressRef
      return;
    }

    // If already connected/connecting to the same address, don't restart
    // We read status here but don't depend on it - we only want to re-run
    // when connection parameters change, not when status changes
    if (
      playerRef.current &&
      currentAddressRef.current === address &&
      (status === "connecting" || status === "connected")
    ) {
      return;
    }

    // Reset state for new connection
    setStatus("idle");
    setError(null);
    setRetryCount(0);

    // Reset whipwhap workaround flags
    whipwhapFirstAttemptRef.current = false;
    connectionStartTimeRef.current = 0;

    // Clear any pending retries
    cleanup();

    // Store the address we're connecting to and increment connection ID
    currentAddressRef.current = address;
    connectionIdRef.current++;
    const thisConnectionId = connectionIdRef.current;

    console.log(
      `[WebRTC Connection #${thisConnectionId}] Starting connection to ${address}`
    );
    setStatus("connecting");

    // Use extracted connection function
    connectWebRTC(thisConnectionId, address, audioElement);

    // Cleanup on unmount or dependency change
    return () => {
      setStatus("disconnected");
      cleanup();
    };
    // Note: connectWebRTC not in deps because it never changes (stable via useCallback with empty deps)
    // Note: status not in deps - see explanation at top of effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, authenticated, address, audioElement, cleanup]);

  // Retry logic - separate effect to avoid re-triggering connection
  useEffect(() => {
    if (status !== "error" || !error || !enabled || !address || !audioElement) {
      return;
    }

    // Don't retry if we've exceeded max retries
    if (retryCount >= MAX_RTC_RETRIES) {
      console.error("Max WebRTC retries reached, giving up");
      diagnosticEventLog.record("webrtc:audio", "error", "max-retries-reached", { retryCount });
      const maxRetryDiagnostics = getPlayerFailureDiagnostics(
        address,
        "webrtc",
        error?.message ?? ""
      );
      Sentry.captureException(
        new Error(`WebRTC player failed after ${MAX_RTC_RETRIES} reconnection attempts`),
        {
          tags: buildPlayerFailureTags(
            "useWebRTCPlayer",
            "webrtc_max_retries_exhausted",
            "webrtc",
            maxRetryDiagnostics
          ),
          extra: {
            lastError: error?.message,
            address,
            retryCount,
            networkDiagnostics: maxRetryDiagnostics.network,
          },
          fingerprint: ["useWebRTCPlayer", "max-retries-exhausted"],
        }
      );
      return;
    }

    const delay = getRetryDelay(retryCount);
    console.log(
      `Retrying WebRTC connection in ${(delay / 1000).toFixed(1)}s... (attempt ${retryCount + 1}/${MAX_RTC_RETRIES})`
    );
    diagnosticEventLog.record("webrtc:audio", "info", "retry-scheduled", { retryCount: retryCount + 1, maxRetries: MAX_RTC_RETRIES, delayMs: delay });

    retryTimeoutRef.current = setTimeout(() => {
      if (!enabled) return;

      setRetryCount((prev) => prev + 1);
      setStatus("connecting");
      setError(null);

      // Update the address ref and connection ID for retry
      currentAddressRef.current = address;
      connectionIdRef.current++;
      const thisConnectionId = connectionIdRef.current;

      console.log(
        `[WebRTC Retry #${thisConnectionId}] Starting retry attempt ${retryCount + 1}/${MAX_RTC_RETRIES} to ${address}`
      );

      // Use extracted connection function for retry
      if (enabled) {
        connectWebRTC(thisConnectionId, address, audioElement);
      }
    }, delay);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [
    status,
    error,
    retryCount,
    enabled,
    address,
    audioElement,
    connectWebRTC,
  ]);

  // Visibility-based reconnection — detect dead connections after background tab throttling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!enabled || !address || !audioElement) return;

      // If retries exhausted while backgrounded, give a fresh start
      if (status === "error") {
        diagnosticEventLog.record("webrtc:audio", "info", "visibility-reconnect", {
          previousStatus: status,
          retryCount,
        });
        cleanup();
        setRetryCount(0);
        setError(null);
        setStatus("idle");
        return;
      }

      // If connected, check whether the MediaStreamTrack is still alive
      if (status === "connected" && audioElement.srcObject) {
        const tracks = (audioElement.srcObject as MediaStream).getTracks();
        const hasDeadTrack =
          tracks.length === 0 ||
          tracks.some((t) => t.readyState === "ended");
        if (hasDeadTrack) {
          diagnosticEventLog.record(
            "webrtc:audio",
            "warn",
            "visibility-dead-track",
            { trackCount: tracks.length }
          );
          cleanup();
          setRetryCount(0);
          setError(null);
          setStatus("idle");
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, address, audioElement, status, cleanup]);

  return {
    status,
    error,
    retryCount,
    setVolume,
  };
};
